import { Injectable, OnDestroy } from "@angular/core";
import { Guid } from "moo-shared";
import { Observable, Subscription, catchError, finalize, interval, map, of, shareReplay, switchMap, tap, throwError } from "rxjs";
import { isDefined, isEmptyString } from "../../shared/helpers";
import { OrganisationTokenUnavailableError } from "../errors/organisation-token-unavailable-error";
import { AuthenticationApiService } from "./authentication-api.service";
import { IIpAccessTokenDto } from "./ip-access-token.dto";
import { IIpOrganisationDto } from "./ip-organisation.dto";

@Injectable()
export class IpTokenService implements OnDestroy {
  ipIsChecked = false;
  private token = "";
  private availableOrganisations$?: Observable<IIpOrganisationDto[]>;
  private tokenRefreshObservable!: Observable<number>;
  private refreshSubscription!: Subscription;
  private multipleOrganisationsAvailable = false;

  get currentToken(): string {
    return this.token;
  }
  tokenRefreshInterval = 720_000;
  constructor(
    private authenticationApiService: AuthenticationApiService
  ) { }
  updateTokenRefreshLoop() {
    if (isDefined(this.tokenRefreshObservable)) {
      return;
    }
    this.tokenRefreshObservable = interval(this.tokenRefreshInterval);
    this.refreshSubscription = this.tokenRefreshObservable.subscribe(() => {
      this.fetchIpToken().subscribe();
    });
  }

  ngOnDestroy(): void {
    if (isDefined(this.refreshSubscription)) {
      this.refreshSubscription.unsubscribe();
    }
  }

  hasMultipleOrganisations(): boolean {
    return this.multipleOrganisationsAvailable;
  }

  isOrganisationSelectionRequired(): boolean {
    return this.multipleOrganisationsAvailable && isEmptyString(this.token);
  }

  getIpToken(): Observable<string> {
    if (!isEmptyString(this.token)) {
      return of(this.token);
    }

    if (this.isOrganisationSelectionRequired()) {
      return of("");
    }

    return this.fetchIpToken().pipe(finalize(() => this.ipIsChecked = true));
  }

  getAvailableOrganisations(): Observable<IIpOrganisationDto[]> {
    if (!this.availableOrganisations$) {
      this.availableOrganisations$ = this.authenticationApiService.getOrganisationsBasedOnIp().pipe(
        tap((organisations: IIpOrganisationDto[]) => {
          this.multipleOrganisationsAvailable = organisations.length > 1;
        }),
        catchError(() => {
          this.multipleOrganisationsAvailable = false;
          return of([]);
        }),
        shareReplay(1)
      );
    }

    return this.availableOrganisations$;
  }

  selectOrganisationAndFetchToken(organisationKey: Guid): Observable<string> {
    return this.authenticationApiService.getTokenForOrganisation(organisationKey)
      .pipe(
        map((tokenInfo: IIpAccessTokenDto) => {
          this.token = tokenInfo.AccessToken;
          return this.token;
        }),
        tap(() => this.updateTokenRefreshLoop()),
        catchError(() => {
          this.emptyToken();
          return throwError(() => new OrganisationTokenUnavailableError());
        })
      );
  }

  private fetchIpToken(): Observable<string> {
    return this.getAvailableOrganisations()
      .pipe(
        switchMap((organisations: IIpOrganisationDto[]) => {
          const organisationKeys = organisations.map(x => x.Key);

          if (organisationKeys.length === 1) {
            return this.selectOrganisationAndFetchToken(organisationKeys[0]);
          }

          return of(this.emptyToken());
        }),
        catchError(() => {
          this.multipleOrganisationsAvailable = false;
          return of(this.emptyToken());
        })
      );
  }

  private emptyToken(): string {
    this.token = "";
    this.availableOrganisations$ = undefined;
    return "";
  }
}
